import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, MessageSquare, AtSign, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Mentions bell — badges unread mentions, opens a small inbox dropdown. */
export default function MentionsInbox() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const auth = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/inbox`, { headers: auth() });
      setData(res.data);
    } catch (_e) { /* silent — inbox is optional UX */ }
  };

  useEffect(() => {
    load();
    // Poll every 60s while page open.
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClickOut = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClickOut);
    return () => document.removeEventListener("mousedown", onClickOut);
  }, [open]);

  const markRead = async () => {
    try {
      await axios.post(`${API}/inbox/mark-read`, {}, { headers: auth() });
      load();
    } catch (_e) { /* silent */ }
  };

  const openMention = (m) => {
    setOpen(false);
    navigate(`/editor/${m.project_id}`);
  };

  const unread = data?.unread || 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-slate-800 transition"
        data-testid="mentions-bell"
        aria-label="Mentions inbox"
      >
        <Bell className="w-5 h-5 text-slate-300" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
            data-testid="mentions-unread-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-50 w-[360px] max-w-[90vw] rounded-lg border border-slate-800 bg-slate-900 shadow-2xl overflow-hidden"
          data-testid="mentions-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-100">
              <AtSign className="w-4 h-4 text-amber-400" />
              Mentions
              {unread > 0 && <span className="text-xs text-slate-500">({unread} new)</span>}
            </div>
            {unread > 0 && (
              <Button
                variant="ghost" size="sm"
                onClick={markRead}
                className="text-xs text-slate-400 hover:text-slate-100 h-7 px-2"
                data-testid="mentions-mark-read"
              >
                <Check className="w-3 h-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {!data?.mentions?.length ? (
              <div className="text-center py-10 px-4 text-sm text-slate-500">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No mentions yet. When someone @-mentions you in a comment, it&apos;ll show up here.
              </div>
            ) : (
              <ul>
                {data.mentions.map((m) => (
                  <li key={m.comment_id}>
                    <button
                      onClick={() => openMention(m)}
                      className={`w-full text-left px-4 py-3 border-b border-slate-800/60 hover:bg-slate-800/50 transition ${!m.is_read ? "bg-amber-500/5" : ""}`}
                      data-testid={`mention-inbox-${m.comment_id}`}
                    >
                      <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                        <span className="truncate">
                          <span className="text-slate-200 font-medium">{m.author_name || m.author_email || "Someone"}</span>
                          <span className="mx-1">·</span>
                          <span className="text-slate-500">{m.project_name}</span>
                        </span>
                        {!m.is_read && <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0 ml-2" />}
                      </div>
                      <p className="text-sm text-slate-300 line-clamp-2">{m.text}</p>
                      <div className="text-[10px] text-slate-500 mt-1">
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
